import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { SESSION_SUSPECT_EVENT } from '@/lib/constants/auth-events';
import { ACADEMY_CSRF_HEADERS } from '@/lib/constants/api';
import { isUserLoggedIn } from '@/helpers/local-storage';

/**
 * Per-request opt-out of the session re-check below: `skipSessionCheck: true` means "a 401
 * from this call says nothing about the session." Owned by the call site, since it's what
 * knows why its 401 happened — e.g. non-session auth (vendor/service key), or the profile
 * fetch in utils/get-user.ts right after a probe already said `valid`.
 */
export type SessionAwareRequestConfig = AxiosRequestConfig & { skipSessionCheck?: boolean };

// Ensure session cookies are sent for Academy API requests
axios.defaults.withCredentials = true;

function isUnauthorizedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { response?: { status?: number }; status?: number };
  const status = err.response?.status ?? err.status ?? 0;
  return status === 401;
}

/**
 * Global interceptor. Never tears down the session itself — only raises a SUSPICION event;
 * useAuthUser()'s probe renders the actual verdict, keeping one authority for auth state.
 * Only a 401 raises suspicion: any other failure (a validation error, a Contentstack-side
 * fault) says nothing about whether the Academy session died.
 * Emitted as a DOM event, not a queryClient call, since this module is plain/non-React and
 * importable server-side; <SessionHydrator/> owns the listener.
 */
axios.interceptors.response.use(
  (res) => res,
  (error) => {
    // Only a 401, in a browser, not opted out, and while logged in is worth flagging.
    if (typeof window === 'undefined') return Promise.reject(error);
    if (!isUnauthorizedError(error)) return Promise.reject(error);
    if ((error?.config as SessionAwareRequestConfig | undefined)?.skipSessionCheck) return Promise.reject(error);
    if (!isUserLoggedIn()) return Promise.reject(error);
    // Coalescing happens on the listener side (SessionHydrator), not here — dispatching is free.
    window.dispatchEvent(new CustomEvent(SESSION_SUSPECT_EVENT));

    return Promise.reject(error);
  }
);

const axiosHandler = async (options: AxiosRequestConfig): Promise<AxiosResponse | AxiosError> => {
  try {
    options.headers = {
      'Accept': 'application/json',
      ...(options?.headers || {}),
      ...ACADEMY_CSRF_HEADERS,
    };
    const res = await axios(options);
    return res;
  } catch (error) {
    return error as AxiosError;
  }
};

export default axiosHandler;
